import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useContent, useOptionalLabel, usePercent, useT } from '@/lib/i18n';
import type { RecommendedPathway } from '@/lib/api';
import type { DisplayDimension } from '@/features/result/viz';
import { useOptionalBandLabel } from '@/features/result/labels';

/**
 * The interpretation half of a result (Workstream N2).
 *
 * Two fields the plugins already model and the console used to drop on the
 * floor: `narrativeKeys` on one score block, `recommendedPathways` on another.
 *
 * ### Presence, never a model name
 *
 * Nothing here asks *which model produced this*. Both components take the field
 * and render only if it carries something — which is the same boundary
 * `registry.get(module.modelType)` protects on the server, and the one
 * `harness-viz.test.tsx` enforces by failing the build if a model name appears
 * anywhere in `src/`. A fourth plugin that emits `narrativeKeys` lights this up
 * with no edit to this file.
 *
 * ### Empty renders NOTHING — not an empty panel
 *
 * Both fields are `.default([])` in the contract and both plugins hard-code them
 * empty today: the ESCO crosswalk is unpopulated and the wording bank is a
 * content dependency. A panel reading "no pathways found" would tell a
 * counsellor the platform looked and found none. It never looked. So an empty
 * array produces no heading, no card, and no skeleton — the screen is shorter,
 * which is the truth.
 */

/* --------------------------------------------------------------- narrative */

/**
 * A narrative key is a LOOKUP KEY, not prose — `conscientiousness.high`.
 *
 * `08 §1.5` defines the bank it resolves against: a `narrativekeys` collection
 * of `{key, text:{en,ar}, modelType, version}` that the CONSUMER renders
 * locale-side. **That collection and its endpoint do not exist in this
 * platform**, and inventing the copy is not open to us either — interpretation
 * wording is authored content owned by a psychometrician, not a string a screen
 * may make up. Rendering `conscientiousness.high` verbatim to a counsellor is
 * the other unacceptable answer.
 *
 * So this resolves the way `useDimensionLabel` resolves an open registry —
 * catalogue first, then a safe fallback, then nothing:
 *
 * 1. **`narrative.<key>` in the console string catalogue.** The authored
 *    sentence, bilingual and reviewed like every other string here. This is
 *    where the wording bank lands client-side, and the seam an endpoint-backed
 *    bank would later be substituted at. **It currently holds no entries**, on
 *    purpose: the copy is a content deliverable, and seeding it with invented
 *    prose to make this section visible would be the fabrication N2 forbids.
 * 2. **The key's own structure.** `<dimension>.<band>` decomposes into two
 *    things the console already has reviewed bilingual labels for, and both
 *    halves come from the result itself — so the entry states the band the
 *    instrument assigned and claims nothing beyond it. The dimension resolves
 *    through the envelope's own `display` labels as well as the catalogue,
 *    which is what makes this work for a model added tomorrow.
 * 3. **Nothing.** A key neither half of which resolves is dropped rather than
 *    printed as a slug. It stays visible in the technical read, where a raw key
 *    is provenance rather than a sentence someone is asked to read aloud.
 */
export type NarrativeEntry =
  | { kind: 'text'; key: string; text: string }
  | { kind: 'band'; key: string; dimension: string; band: string };

/**
 * `useOptionalLabel`, not `useLabel`, and it is not a style preference.
 *
 * Every resolution here has to be able to answer *no* — a key that resolves to
 * nothing is dropped. `useLabel` echoes an unrecognised value back, so "resolved"
 * and "unresolved" would be told apart by comparing the answer to the input,
 * and `band.High` translates to the string `High`: identity comparison would
 * have silently dropped every English band label in the catalogue.
 */

/**
 * The band half is `useOptionalBandLabel` (`features/result/labels.ts`), which is
 * where it moved when the headline needed the same answer.
 *
 * Its case tolerance is the reason it is worth sharing rather than re-deriving:
 * `08` writes interpretation keys lower-case (`conscientiousness.high`) while
 * the contract's `Band` / `SkillBand` enums are capitalised. Both spellings are
 * in the wild, and each place that resolves a band has to know it.
 */

export function useNarrativeEntries(): (
  keys: string[],
  dimensions: DisplayDimension[],
) => NarrativeEntry[] {
  const optional = useOptionalLabel();
  const content = useContent();
  const bandOf = useOptionalBandLabel();

  return (keys, dimensions) => {
    const labels = new Map(dimensions.map((d) => [d.key, d.label]));

    return keys.flatMap<NarrativeEntry>((key) => {
      // 1 — the wording bank, if this console has the sentence.
      const authored = optional('narrative', key);
      if (authored) return [{ kind: 'text', key, text: authored }];

      // 2 — the key's own structure. `lastIndexOf`, so a dimension key that
      // itself contains a dot keeps the band as the tail.
      const split = key.lastIndexOf('.');
      if (split <= 0) return [];

      const head = key.slice(0, split);
      // `useDimensionLabel`'s order — catalogue, then the label the envelope
      // itself carried — with its last step (the raw key) replaced by nothing.
      // That second source is what makes this work for a model registered
      // tomorrow, whose dimensions this catalogue has never heard of.
      const dimension = optional('dimension', head) || content(labels.get(head));
      const band = bandOf(key.slice(split + 1));
      if (!dimension || band === null) return [];

      return [{ kind: 'band', key, dimension, band }];
    });
  };
}

export function NarrativeCard({
  narrativeKeys,
  dimensions,
}: {
  narrativeKeys: string[] | undefined;
  dimensions: DisplayDimension[];
}) {
  const t = useT();
  const entries = useNarrativeEntries()(narrativeKeys ?? [], dimensions);

  // No keys, or none of them resolvable: no card. Not an empty one.
  if (entries.length === 0) return null;

  const pending = entries.some((entry) => entry.kind === 'band');

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">{t('result.narrative')}</CardTitle>
        <CardDescription>{t('result.narrative.hint')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {entries.map((entry) => (
            <li key={entry.key} className="text-sm">
              {entry.kind === 'text' ? (
                entry.text
              ) : (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{entry.dimension}</span>
                  <Badge variant="secondary">{entry.band}</Badge>
                </span>
              )}
            </li>
          ))}
        </ul>

        {/* Said only when it is true, and it disappears the day the copy lands.
            A reader is owed the difference between "this is the interpretation"
            and "this is the band the interpretation would have been written
            about". */}
        {pending && (
          <p className="text-muted-foreground mt-3 text-xs">{t('result.narrative.pending')}</p>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------- pathways */

/**
 * Occupations the profile aligns with.
 *
 * `escoUri` is a URI, which is provenance and not a heading — a counsellor
 * reading `http://data.europa.eu/esco/occupation/…` aloud is the failure. The
 * localised label leads; the URI sits underneath in the same treatment the
 * Provenance card gives an id (monospace, `dir="ltr"`, muted), because that is
 * what it is.
 */
export function PathwaysCard({ pathways }: { pathways: RecommendedPathway[] | undefined }) {
  const t = useT();
  const content = useContent();
  const percent = usePercent();

  // Empty until the ESCO crosswalk is populated, which is the plugin's own
  // comment. Degrade to nothing, exactly as it degrades to nothing.
  if (!pathways || pathways.length === 0) return null;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">{t('result.pathways')}</CardTitle>
        <CardDescription>{t('result.pathways.hint')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {pathways.map((pathway) => (
            <li key={pathway.escoUri} className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium">{content(pathway.label)}</p>
                <p className="text-muted-foreground font-mono text-xs break-all" dir="ltr">
                  {pathway.escoUri}
                </p>
              </div>
              {/* `fitScore` is 0..1 in the contract. Rendering it as a
                  percentage is a DELIBERATE conversion — ×100 here, and through
                  `usePercent` so the sign lands on the side the language puts
                  it and cannot be reordered by whatever runs beside it. */}
              <Badge variant="secondary" className="shrink-0">
                {t('result.pathways.fit', { fit: percent(pathway.fitScore * 100) })}
              </Badge>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
